import TwoColumnLayout from "layout/TwoColumnLayout";
import React from "react";
import moment from "moment";
import { type ShowDates } from "components/showdatepicker/models";
import { useValidators } from "formvalidation/hooks";
import ValidatorContext from "formvalidation/FormContext";
import EditViewPanel from "components/panels/EditViewPanel";
import CollapsibleSection from "components/DetailPage/CollapsibleSection";
import { useFreefieldState } from "shared/utils/freeFields/hooks";
import FreefieldPanel from "./FreefieldPanel";
import { Button } from "semantic-ui-react";
import { useMutation } from "@tanstack/react-query";
import { routes } from "utilities/routing/generatedRoutes";
import GeneralOptionsPanel from "components/optionspanel/GeneralOptionsPanel";
import MainDetailsPanel from "./External/MainDetailsPanel";
import { ExternalShowInfo, mapExternalShowInfoToInsertShowExternalInstruction } from "./utils";

interface Props {
  initialValues: ExternalShowInfo
  ffCategories: server.dto.FreeFieldCategorySmall[]
  ffPermissions: server.dto.FreeFieldDefinitionPermission[]
  optionsPanel: server.dto.OptionsPanelDataV2
  listUrl: string
  statuses: server.dto.StatusSmall[]
}

const ShowExternalEditApp = ({ ffCategories, ffPermissions, initialValues, optionsPanel, statuses, listUrl }: Props) => {
  const [showDates, setShowDates] = React.useState<ShowDates>({
    startDisplayDate: moment.utc(initialValues.showDates.startDisplayDate),
    endDisplayDate: moment.utc(initialValues.showDates.endDisplayDate),
    timeOfPerformance: initialValues.showDates.timeOfPerformance,
    mode: initialValues.showDates.mode
  });
  const [production, setProduction] = React.useState(initialValues.production);

  const [artist, setArtist] = React.useState(initialValues.artist);

  const [status, setStatus] = React.useState(initialValues.status);
  const [eventName, setEventName] = React.useState(initialValues.eventName);
  const [eventWebsite, setEventWebsite] = React.useState(initialValues.eventWebsite ?? "");

  const freefieldState = useFreefieldState(initialValues.freefieldValues, ffCategories, ffPermissions);

  const createMutation = useMutation(window.CoreApi.Show.InsertForExternalEnvironment);

  const [stayLoading, setStayLoading] = React.useState(false);

  const [isGoingtoSubmit, setIsGoingToSubmit] = React.useState(false);
  /**
     * Ugly hack to handle the following case:
     * 1. Focus is in an input field that updates state onBlur
     * 2. User presses enter
     * 3. Form is submitted before the new state is set
     */
  React.useEffect(() => {
    if (isGoingtoSubmit) {
      onSubmit();
      setIsGoingToSubmit(false);
    }
  }, [isGoingtoSubmit]);

  const onSubmit = async () => {
    if (isSaving) return;

    const res = await validators.validateAll();

    if (res.hasErrors) {
      res.scrollToFirstError();
      return;
    }

    const response = await createMutation.mutateAsync(
      mapExternalShowInfoToInsertShowExternalInstruction({
        showDates,
        production,
        artist,
        status,
        eventName,
        eventWebsite,
        freefieldValues: freefieldState.editableFreefieldValues
      })
    );

    if (!response.data.hasErrors) {
      setStayLoading(true);
      window.location.href = routes.show_Detail(response.data.data).absolute;
    }
  };

  const onTimeOfPerformanceChange = (newTimeOfPerformance: string) => {
    setShowDates(prevDates => ({
      ...prevDates,
      timeOfPerformance: newTimeOfPerformance
    }));
  };

  const validators = useValidators();

  const isSaving = createMutation.isLoading || stayLoading;

  return (
    <ValidatorContext settings={validators.validatorSettings}>
      <TwoColumnLayout>
        <TwoColumnLayout.LeftColumn>
          <EditViewPanel title={site.currentEnvironment.show.singular}>
            <form onSubmit={e => {
              e.preventDefault();
              setIsGoingToSubmit(true);
            }} className="flex-column gap-bigger">
              <CollapsibleSection
                uniqueRowId="showEditMainDetials"
                collapsible="no"
                type="form">
                <MainDetailsPanel
                  production={production}
                  onProductionChanged={setProduction}

                  readonlyArtist={false}
                  artist={artist}
                  onArtistChanged={setArtist}

                  dates={showDates}
                  onDatesChanged={setShowDates}
                  onTimeOfPerformanceChanged={onTimeOfPerformanceChange}

                  status={status}
                  onStatusChanged={setStatus}
                  statuses={statuses}

                  initialEventName={eventName}
                  onEventNameChanged={setEventName}

                  eventWebsite={eventWebsite}
                  onEventWebsiteChanged={setEventWebsite}
                />
              </CollapsibleSection>

              {freefieldState.freefieldCategoriesWithValuesAndPermissions.map(cat => (
                <CollapsibleSection
                  key={cat.freeFieldCategoryId}
                  title={cat.title}
                  uniqueRowId={`showEditFreefieldCategory${cat.freeFieldCategoryId}`}
                  collapsible="no"
                  type="form">
                  <FreefieldPanel freefieldCategoryWithValueAndPermission={cat} freefieldState={freefieldState} />
                </CollapsibleSection>
              ))}
              <div className="flex justify-end">
                <Button type="submit" disabled={isSaving} loading={isSaving} primary>{RESX.Buttons.btnSave}</Button>
              </div>
            </form>
          </EditViewPanel>
        </TwoColumnLayout.LeftColumn>

        <TwoColumnLayout.RightColumn>
          <GeneralOptionsPanel
            data={optionsPanel}
            links={[
              {
                text: RESX.Buttons.ListView,
                href: listUrl,
                visible: true,
              }
            ]}
          />
        </TwoColumnLayout.RightColumn>
      </TwoColumnLayout>
    </ValidatorContext>
  );
};

export default ShowExternalEditApp;
